import React from 'react';
import { PropTypes } from 'prop-types';
// styles
import styled from 'styled-components';
// state
// components
import { CheckCircleIcon } from 'refactored/ui/icons';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import SoFormButton from 'refactored/features/soForm/components/other/SoFormButton';
import SoFormContainer from 'refactored/features/soForm/components/layout/SoFormContainer';
import { useDispatch } from 'react-redux';
import routes from 'utils/routes';
import { push } from 'connected-react-router';

const MainContainer = styled.div`
  /* Frame 188 */

  /* Auto layout */

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 48px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 16px;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;

  width: 544px;
  height: 151px;
`;

const HeadingTextBig = styled.div`
  height: 40px;

  /* 32px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 32px;
  line-height: 40px;
  /* identical to box height, or 125% */

  text-align: center;

  /* primary/500 */

  color: var(--primary-btn-main-menu);
`;
const HeadingTextRegular = styled.div`
  height: 32px;

  /* 14px/regular */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  /* or 114% */

  text-align: center;

  /* grey / 700 */

  color: #54708c;
  display: flex;
  align-items: center;
`;

const IdContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;

  height: 36px;

  /* grey / 200 */

  background: #e2e8ee;
  border-radius: 4px;
`;

const IdText = styled.div`
  height: 16px;

  /* 14px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  /* identical to box height, or 114% */

  /* grey / 700 */

  color: #54708c;
`;

const StopsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 16px;
`;
const StopsInnerContainer = styled.div`
  /* Auto layout */

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px 32px 24px;
  gap: 24px;

  /* Flowkit/White */

  background: #ffffff;
  border-radius: 4px;
`;

const StopsItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
`;

const StopInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
`;

const StopTitle = styled.div`
  width: 480px;
  height: 24px;

  /* 20px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  /* identical to box height, or 120% */

  display: flex;
  align-items: center;

  /* grey / 800 */

  color: #3b4f63;
`;

const StopDate = styled.div`
  width: 480px;
  height: 24px;

  /* 16px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  display: flex;
  align-items: center;

  /* grey / 500 */

  color: #94a6b8;
`;

const StopSeparator = styled.div`
  height: 0px;

  /* grey / 200 */

  border: 1px solid #e2e8ee;
`;

const getFormattedDateFromDate = (date) => {
  return moment(date).format('dddd D MMMM');
};

const getFormattedRangeFromHoursAndInitialDate = (date, timeFrom, timeTo) => {
  const dateFrom = `${date} ${timeFrom}`;
  const dateTo = `${date} ${timeTo}`;
  return `${moment(dateFrom).format('HH:mm')} - ${moment(dateTo).format(
    'HH:mm'
  )}`;
};

const Stops = ({ ticketFormData }) => {
  const { t } = useTranslation('Emergencies');
  const { createdTicket } = ticketFormData;
  const pickupTittle = createdTicket.order_data.pickup.city;
  const dropOffTittle = createdTicket.order_data.dropoff.city;

  const pickupDate = `${getFormattedDateFromDate(
    createdTicket.order_data.pickup.date
  )}, ${t('pickup')} ${getFormattedRangeFromHoursAndInitialDate(
    createdTicket.order_data.pickup.date,
    createdTicket.order_data.pickup.time_from,
    createdTicket.order_data.pickup.time_to
  )}`;
  const dropoffDate = `${getFormattedDateFromDate(
    createdTicket.order_data.dropoff.date
  )}, ${t('dropoff')} ${getFormattedRangeFromHoursAndInitialDate(
    createdTicket.order_data.dropoff.date,
    createdTicket.order_data.dropoff.time_from,
    createdTicket.order_data.dropoff.time_to
  )}`;

  return (
    <StopsContainer>
      <StopsInnerContainer>
        <StopsItemsContainer>
          <Details>
            <StopInfo>
              <StopTitle>
                {t('pickup')} {pickupTittle}
              </StopTitle>
              <StopDate>{pickupDate}</StopDate>
            </StopInfo>
            <StopSeparator />
            <StopInfo>
              <StopTitle>
                {t('dropoff')} {dropOffTittle}
              </StopTitle>
              <StopDate>{dropoffDate}</StopDate>
            </StopInfo>
          </Details>
        </StopsItemsContainer>
      </StopsInnerContainer>
    </StopsContainer>
  );
};

const Heading = () => {
  const { t } = useTranslation('Emergencies');
  return (
    <HeadingContainer>
      <CheckCircleIcon />
      <HeadingTextBig>{t('confirmation_header')}</HeadingTextBig>
      <HeadingTextRegular>{<>{t('confirmation_body')}</>}</HeadingTextRegular>
    </HeadingContainer>
  );
};

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 8px;
  ${SoFormButton} {
    width: unset;
  }
`;

const Actions = ({ ticketFormData }) => {
  const dispatch = useDispatch();

  const { t } = useTranslation('Emergencies');

  const goToOrders = () => {
    dispatch(push(`${routes.emergencies}/`));
  };

  const reset = () => {
    ticketFormData.reset();
  };

  return (
    <ActionsContainer>
      <SoFormButton inverse={true} onClick={goToOrders}>
        {t('back_to_orders')}
      </SoFormButton>
      <SoFormButton onClick={reset}>{t('request_new_budget')}</SoFormButton>
    </ActionsContainer>
  );
};

const TicketConfirmation = ({ className, ticketFormData }) => {
  const createdTicket = ticketFormData.createdTicket;
  const id = createdTicket.id;
  const { t } = useTranslation('Emergencies');

  return (
    <div className={className}>
      <SoFormContainer>
        <MainContainer>
          <InfoContainer>
            <Heading />
            <IdContainer>
              <IdText>
                {t('ticket_id')}: {id}
              </IdText>
            </IdContainer>
            <Stops ticketFormData={ticketFormData} />
          </InfoContainer>
          <Actions ticketFormData={ticketFormData} />
        </MainContainer>
      </SoFormContainer>
    </div>
  );
};

TicketConfirmation.propTypes = {
  className: PropTypes.string,
  ticketFormData: PropTypes.object,
};

export default styled(TicketConfirmation)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 0px;
  padding-top: 96px;
  width: 100%;
  background-color: var(--grey100);
  svg path {
    fill: var(--primary-btn-main-menu);
  }
  ${SoFormContainer} {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0px;
    gap: 16px;
    padding: 0;
  }
  ${SoFormButton} {
    width: 50%;
  }
  ${SoFormButton}:last-child {
    background: var(--primary-btn-main-menu);
  }
  ${SoFormButton}:first-child {
    border-color: var(--primary-btn-main-menu);
  }
  flex-grow: 1;
`;
